import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Copy to Shipments (user, 2026-10-22): link the invoice's tracking number to
 * its customer through a work order. Worked per tracking number: when every
 * Nova Shipping row with the number belongs to ONE customer (the store's
 * work orders, 278 Laredo Dr, left out), ONE Shipments row is written -- the
 * 1Z number under PK-UPS, one of the customer's W-IDs and the customer.
 * Several customers = a consolidated box: copied under Krown's customer
 * C-377 with the latest W-ID (user, 2026-11-03). What would be
 * written comes from /bundle-sync/shipments-copy-preview; nothing is written
 * until "Copy".
 */
export default function CopyToShipmentsModal({ invoiceNumber, onHide, onCopied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await API.post(
        "/bundle-sync/shipments-copy-preview",
        { invoiceNumber },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Could not load the invoice");
      setPlan(data.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [invoiceNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async () => {
    setCopying(true);
    setError("");
    try {
      const { data } = await API.post(
        "/bundle-sync/shipments-copy",
        { invoiceNumber },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Copy failed");
      setPlan(data.data);
      setDone(data.message);
      onCopied?.(data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setCopying(false);
    }
  };

  // Why the button is off, said on the page rather than left to a 400.
  const blocker =
    !plan || plan.copied
      ? ""
      : !plan.trackingNumber
        ? `AWB ${plan.awbNumber || "(none)"} is not a UPS or DHL parcel number (air cargo?) — there is nothing to track.`
        : plan.missingConsolidatedCustomer
          ? "This is a consolidated box (several customers). These go to customer C-377, which was not found — add it, then copy."
          : plan.status === "no_customer"
            ? "No customer to link this tracking number to (only the store's work order, or none linked)."
            : !plan.courier
              ? `There is no courier named ${plan.courierName}. Add it in Settings → Couriers (Tracking API: ${plan.provider === "dhl" ? "DHL" : "UPS"}).`
              : "";

  const field = (label, value) => (
    <div className="d-flex gap-2 py-1 border-bottom">
      <div className="text-muted" style={{ flex: "0 0 140px" }}>{label}</div>
      <div className="fw-semibold">{value}</div>
    </div>
  );

  return (
    <>
      <style>
        {`
          .ns-copyship-backdrop { z-index: 100050; }
          .ns-copyship-modal    { z-index: 100051; }
        `}
      </style>
      <Modal
        show
        onHide={copying ? undefined : onHide}
        centered
        backdropClassName="ns-copyship-backdrop"
        className="ns-copyship-modal"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton={!copying}>
          <Modal.Title className="h5 mb-0">
            Copy to Shipments
            <span className="text-muted fs-6 ms-2">Invoice {invoiceNumber}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && <div className="text-center text-muted py-4">Loading the invoice…</div>}
          {!loading && error && <div className="alert alert-danger py-2">{error}</div>}
          {!loading && done && <div className="alert alert-success py-2">{done}</div>}
          {!loading && blocker && <div className="alert alert-warning py-2">{blocker}</div>}

          {!loading && plan && (
            <>
              {field("Tracking #", plan.trackingNumber || plan.awbNumber || "—")}
              {plan.invoiceNumbers?.length > 1 &&
                field("Invoices", plan.invoiceNumbers.join(", "))}
              {field("Courier", plan.courier?.name || `${plan.courierName || "—"} (missing)`)}
              {/* A consolidated box (several customers) goes in under Krown,
                  C-377, with one of its W-IDs (user, 2026-11-03). */}
              {plan.status === "consolidated" && (
                <div className="alert alert-info py-2">
                  Consolidated box for {plan.customerNames.length} customer
                  {plan.customerNames.length === 1 ? "" : "s"}
                  {plan.destinationCount > plan.customerNames.length
                    ? ` at ${plan.destinationCount} addresses`
                    : ""}
                  : copied under <b>C-377</b>.
                  <div className="small text-muted mt-1">{plan.customerNames.join(", ")}</div>
                </div>
              )}
              {
                <>
                  {field(
                    "Work order",
                    plan.woId ? (
                      `W-${plan.woId}${plan.workOrderCount > 1 ? ` (1 of ${plan.workOrderCount})` : ""}`
                    ) : (
                      <span className="text-muted fw-normal">—</span>
                    ),
                  )}
                  {field(
                    "Customer",
                    plan.customerNumber
                      ? `C-${plan.customerNumber} · ${plan.customerName || ""}`
                      : plan.customerName || <span className="text-muted fw-normal">—</span>,
                  )}
                </>
              }
              {field("Packages", plan.packages || "—")}
              {plan.storeWoIds?.length > 0 && plan.status !== "no_customer" && (
                <p className="small text-muted mt-2 mb-0">
                  Not linked to the store's work order
                  {plan.storeWoIds.length === 1 ? "" : "s"} (
                  {plan.storeWoIds.map((n) => `W-${n}`).join(", ")}, 278 Laredo Dr).
                </p>
              )}
              {plan.copied && (
                <div className="alert alert-info py-2 mt-3 mb-0">
                  <i className="mdi mdi-truck-check-outline me-1" aria-hidden="true" />
                  Already in Shipments
                  {plan.copied.woId ? ` with W-${plan.copied.woId}` : ""}.
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide} disabled={copying}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading || copying || !!blocker || !plan || !!plan.copied}
            onClick={copy}
          >
            {copying ? "Copying…" : plan?.copied ? "In Shipments" : "Copy to Shipments"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
